'use client';

import { useState, useCallback, useRef } from 'react';

export interface TerminalLine {
  id: string;
  content: string;
  type: 'input' | 'output' | 'system' | 'error' | 'amber' | 'cyan' | 'red';
}

export function useTerminal() {
  const [history, setHistory] = useState<TerminalLine[]>([]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const idCounter = useRef(0);

  const addLine = useCallback((content: string, type: TerminalLine['type'] = 'output') => {
    const id = `line-${idCounter.current++}`;
    setHistory((prev) => [...prev, { id, content, type }]);
    return id;
  }, []);

  const addLines = useCallback((lines: string[], type: TerminalLine['type'] = 'output') => {
    const newLines = lines.map((content) => ({
      id: `line-${idCounter.current++}`,
      content,
      type,
    }));
    setHistory((prev) => [...prev, ...newLines]);
  }, []);

  const addCommand = useCallback((command: string) => {
    setCommandHistory((prev) => [...prev, command]);
    setHistoryIndex(-1);
    addLine(`  $ ${command}`, 'input');
  }, [addLine]);

  const clear = useCallback(() => {
    setHistory([]);
  }, []);

  const getPreviousCommand = useCallback(() => {
    if (commandHistory.length === 0) return '';
    const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
    setHistoryIndex(newIndex);
    return commandHistory[newIndex] || '';
  }, [commandHistory, historyIndex]);

  const getNextCommand = useCallback(() => {
    if (historyIndex === -1) return '';
    const newIndex = historyIndex + 1;
    if (newIndex >= commandHistory.length) {
      setHistoryIndex(-1);
      return '';
    }
    setHistoryIndex(newIndex);
    return commandHistory[newIndex] || '';
  }, [commandHistory, historyIndex]);

  return {
    history,
    addLine,
    addLines,
    addCommand,
    clear,
    getPreviousCommand,
    getNextCommand,
  };
}
